import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { Link,useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  FiPlusCircle, 
  FiSearch, 
  FiFilter, 
  FiRefreshCw,
  FiCheckCircle, 
  FiClock, 
  FiXCircle, 
  FiCalendar,
  FiTrash2,
  FiBox,
  FiInfo,
  FiWifi,
  FiAlertTriangle,
  FiUsers
} from 'react-icons/fi';
import { getAllLicenses, deleteLicense ,fetchApplications, updateLicense } from '../../services/licenseService';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';

const LicenseList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  // 状态
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState('');
  const [online, setOnline] = useState('');
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [applicationId, setApplicationId] = useState(searchParams.get('applicationId') || '');
  const [togglingId, setTogglingId] = useState(null); // 跟单开关切换中的授权码ID

// 获取应用列表
const { data: applications } = useQuery('applications', fetchApplications);



const handleApplicationChange = (e) => {
  setApplicationId(e.target.value);
  setPage(1);
  
  // 更新URL参数但不重载页面
  const params = new URLSearchParams(location.search);
  if (e.target.value) {
    params.set('applicationId', e.target.value);
  } else {
    params.delete('applicationId');
  }
  
  // 使用history API更新URL而不重载页面
  window.history.replaceState(
    {},
    '',
    `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`
  );
  
  refetch();
};

  // 获取授权码列表
  const { 
  data, 
  isLoading, 
  error, 
  refetch 
} = useQuery(
  ['licenses', page, limit, status, search, applicationId, online],
  () => getAllLicenses(page, limit, status, search, applicationId, online),
  {
    keepPreviousData: true
  }
);

  // 处理搜索
  const handleSearch = (e) => {
    e.preventDefault();
    refetch();
  };

  // 清除搜索
  const clearSearch = () => {
    setSearch('');
    refetch();
  };

  // 处理状态筛选
  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(1);
    refetch();
  };

  // 处理在线状态筛选
  const handleOnlineChange = (e) => {
    setOnline(e.target.value);
    setPage(1);
    refetch();
  };

  // 删除授权码
  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteLicense(deleteId);
      toast.success('授权码删除成功');
      refetch();
      setShowDeleteModal(false);
      setDeleteId(null);
    } catch (error) {
      toast.error(error.message || '删除授权码失败');
    }
  };
  
  // 切换云端跟单开关(即时生效: 下一轮poll即按新状态过闸口)
  const toggleFollow = async (license) => {
    setTogglingId(license.id);
    try {
      await updateLicense(license.id, { followEnabled: !license.followEnabled });
      toast.success(license.followEnabled ? `已关闭 ${license.customerName || license.licenseKey} 的跟单` : `已开启跟单`);
      refetch();
    } catch (error) {
      toast.error(error.message || '切换跟单开关失败');
    } finally {
      setTogglingId(null);
    }
  };

  // 跟单状态判断: 开关 + 跟单到期
  const followState = (license) => {
    if (!license.followEnabled) return 'off';
    if (license.followExpiresAt && new Date(license.followExpiresAt).getTime() <= Date.now()) return 'expired';
    return 'on';
  };

  // 获取状态标签
  // 临期规则: 已激活且 7 天内到期 -> 红色"已临期"(带剩余天数); 已过期 -> 红色"已过期"
  const getStatusBadge = (status, expiresAt) => {
    if (status === 'active' && expiresAt) {
      const daysLeft = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000);
      if (daysLeft < 0) {
        return <span className="badge badge-expired"><FiXCircle className="mr-1" />已过期</span>;
      }
      if (daysLeft <= 7) {
        return (
          <span className="badge badge-expired badge-expired-dot" title={`距离到期还有 ${daysLeft} 天`}>
            <FiAlertTriangle className="mr-1" />已临期
          </span>
        );
      }
    }
    switch (status) {
      case 'active':
        return <span className="badge badge-active badge-active-dot"><FiCheckCircle className="mr-1" />已激活</span>;
      case 'pending':
        return <span className="badge badge-pending badge-pending-dot"><FiClock className="mr-1" />待授权</span>;
      case 'expired':
        return <span className="badge badge-expired"><FiXCircle className="mr-1" />已过期</span>;
      case 'revoked':
        return <span className="badge badge-revoked"><FiXCircle className="mr-1" />已撤销</span>;
      default:
        return <span className="badge badge-revoked">未知</span>;
    }
  };

  // 获取在线状态标签
  const getOnlineBadge = (isOnline) => {
    if (isOnline) {
      return <span className="badge badge-online badge-online-dot">在线</span>;
    }
    return <span className="badge badge-offline badge-offline-dot">离线</span>;
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '无';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };

  // 页面动画
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full"
    >
      {/* 页面标题 */}
      <motion.div variants={itemVariants} className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2328]">授权码管理</h1>
          <p className="text-[#656d76] mt-1">管理所有授权码，查看状态和详情。</p>
          {isAdmin && (
            <p className="mt-1 text-xs text-[#9a6700] bg-[#fff8c5] rounded-md px-2 py-1 inline-block">
              超级管理员视角：显示全部用户的授权码，可统一管理
            </p>
          )}
        </div>
        <Button
          to="/licenses/create"
          variant="primary"
          icon={<FiPlusCircle />}
        >
          生成授权码
        </Button>
      </motion.div>

      {/* 搜索和筛选 */}
      <motion.div variants={itemVariants} className="bg-white rounded-md p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索客户名称 / 账户 / 授权码..."
                className="w-full py-2 px-4 !pl-10 bg-[#f6f8fa] rounded-md text-[#1f2328] focus:outline-none input-primary"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#656d76]" />
              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#656d76] hover:text-[#1f2328]"
                >
                  &times;
                </button>
              )}
            </div>
          </form>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <FiFilter className="text-[#656d76]" />
              <select
                value={status}
                onChange={handleStatusChange}
                className="bg-[#f6f8fa] rounded-md text-[#1f2328] py-2 px-3 focus:outline-none input-primary"
              >
                <option value="">全部状态</option>
                <option value="active">已激活</option>
                <option value="pending">待授权</option>
                <option value="expired">已过期</option>
                <option value="revoked">已撤销</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <FiWifi className="text-[#656d76]" />
              <select
                value={online}
                onChange={handleOnlineChange}
                className="bg-[#f6f8fa] rounded-md text-[#1f2328] py-2 px-3 focus:outline-none input-primary"
              >
                <option value="">在线状态</option>
                <option value="true">在线</option>
                <option value="false">离线</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2 ml-4">
  <FiFilter className="text-[#656d76]" />
  <select
    value={applicationId}
    onChange={handleApplicationChange}
    className="bg-[#f6f8fa] rounded-md text-[#1f2328] py-2 px-3 focus:outline-none input-primary"
  >
    <option value="">所有应用</option>
    {applications?.map(app => (
      <option key={app.id} value={app.id}>{app.name}</option>
    ))}
  </select>
</div>
            
            

            <Button
              onClick={() => refetch()}
              variant="secondary"
              icon={<FiRefreshCw />}
              className="px-3"
            >
              刷新
            </Button>
          </div>
        </div>
      </motion.div>

      {/* 授权码列表 */}
      <motion.div variants={itemVariants} className="bg-white rounded-md shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            <div className="skeleton h-10" />
            <div className="skeleton h-10" />
            <div className="skeleton h-10" />
            <div className="skeleton h-10" />
            <div className="skeleton h-10" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-red-500 text-lg mb-4">获取授权码列表失败</div>
            <Button onClick={() => refetch()} variant="primary">
              重试
            </Button>
          </div>
        ) : data?.licenses?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#656d76] px-6">
            <div className="w-16 h-16 rounded-md brand-soft brand-text flex items-center justify-center mb-5">
              <FiInfo className="text-3xl" />
            </div>
            <p className="text-xl text-[#24292f] font-medium">没有找到授权码</p>
            <p className="mt-2 text-center">先创建应用，再为该应用生成授权码；授权码激活后会绑定首次验证的设备</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <Button
                to="/applications/create"
                variant="secondary"
                icon={<FiBox />}
              >
                创建应用
              </Button>
              <Button
                to="/licenses/create"
                variant="primary"
                icon={<FiPlusCircle />}
              >
                生成授权码
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="data-table min-w-[900px]">
                <thead>
  <tr>
    <th className="py-3 px-4 text-left">客户名称</th>
    <th className="py-3 px-4 text-left">账户</th>
    <th className="py-3 px-4 text-left">所属应用</th>
    <th className="py-3 px-4 text-left">状态</th>
    <th className="py-3 px-4 text-left">云端跟单</th>
    <th className="py-3 px-4 text-left">在线</th>
    <th className="py-3 px-4 text-left">过期时间</th>
    {isAdmin && <th className="py-3 px-4 text-left">创建者</th>}
    <th className="py-3 px-4 text-right">操作</th>
  </tr>
</thead>
                <tbody>
                  {data.licenses.map((license) => (
                    <motion.tr
                      key={license.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <td className="py-3 px-4" title={license.remark || undefined}>
  {license.customerName ? (
    <span className="text-[#1f2328]">{license.customerName}</span>
  ) : (
    <span className="text-[#8b949e]">—</span>
  )}
  {license.remark && (
    <span className="block text-xs text-[#8b949e] truncate max-w-[10rem]">{license.remark}</span>
  )}
</td>
<td className="py-3 px-4">
  {license.account ? (
    <span className="font-mono text-[#1f2328]">{license.account}</span>
  ) : (
    <span className="text-[#8b949e]">—</span>
  )}
</td>
<td className="py-3 px-4">
  {license.application ? (
    <Link 
      to={`/applications/${license.application.id}`}
      className="px-2 py-1 text-xs rounded-full brand-soft brand-text flex items-center inline-flex hover:opacity-80"
    >
      <FiBox className="mr-1" />
      {license.application.name}
    </Link>
  ) : (
    <span className="text-[#8b949e]">未知应用</span>
  )}
</td>
                      <td className="py-3 px-4">
                        {getStatusBadge(license.status, license.expiresAt)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleFollow(license)}
                            disabled={togglingId === license.id}
                            title={license.followEnabled ? '点击关闭跟单(即时生效)' : '点击开启跟单'}
                            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                              followState(license) === 'on' ? 'bg-[#1a7f37]' : 'bg-[#d0d7de]'
                            }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              followState(license) === 'on' ? 'translate-x-4.5' : 'translate-x-1'
                            }`} />
                          </button>
                          {followState(license) === 'expired' ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ffebe9] text-[#cf222e]">已到期</span>
                          ) : license.followEnabled && license.followGroup ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ddf4ff] text-[#0969da] border border-[#b6e3ff] flex items-center gap-0.5">
                              <FiUsers size={9} />{license.followGroup}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getOnlineBadge(license.isOnline)}
                      </td>
                      <td className="py-3 px-4 text-[#57606a]">
                        {license.expiresAt ? (
                          <div className="flex items-center gap-1">
                            <FiCalendar size={14} />
                            <span>{formatDate(license.expiresAt)}</span>
                          </div>
                        ) : (
                          <span className="text-[#8b949e]">永久有效</span>
                        )}
                      </td>
{isAdmin && (
<td className="py-3 px-4 text-[#57606a]">
  {license.creator ? (
    <span className="inline-flex items-center gap-1">
      {license.creator.id === currentUser?.id && <span className="text-xs brand-text">(我)</span>}
      {license.creator.username}
    </span>
  ) : (
    <span className="text-[#8b949e]">—</span>
  )}
</td>
)}
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/licenses/${license.id}`}
                            className="p-1.5 brand-text hover:opacity-80 hover:bg-[#f6f8fa] rounded-md transition-colors"
                            title="查看详情"
                          >
                            <FiInfo size={18} />
                          </Link>
                          <button
                            onClick={() => {
                              setDeleteId(license.id);
                              setShowDeleteModal(true);
                            }}
                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-[#ffebe9] rounded-md transition-colors"
                            title="删除授权码"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            <div className="p-4 flex flex-wrap justify-between items-center gap-3 border-t border-[#d0d7de]">
              <div className="text-[#656d76]">
                总计 {data.totalItems} 个授权码，共 {data.totalPages} 页
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPage(page > 1 ? page - 1 : 1)}
                  disabled={page <= 1}
                  variant="secondary"
                  size="sm"
                >
                  上一页
                </Button>
                <span className="inline-flex items-center px-3 py-1 rounded bg-[#f6f8fa] text-[#1f2328]">
                  {page} / {data.totalPages || 1}
                </span>
                <Button
                  onClick={() => setPage(page < data.totalPages ? page + 1 : data.totalPages)}
                  disabled={page >= data.totalPages}
                  variant="secondary"
                  size="sm"
                >
                  下一页
                </Button>
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* 删除确认弹窗 */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="删除授权码"
      >
        <div className="mb-6">
          <p className="text-[#57606a]">确定要删除这个授权码吗？此操作不可逆。</p>
          <p className="text-[#cf222e] text-sm mt-2">删除后将同时清除该授权码的所有账户上报数据与指令记录。</p>
        </div>
        <div className="flex justify-end gap-4">
          <Button
            onClick={() => setShowDeleteModal(false)}
            variant="secondary"
          >
            取消
          </Button>
          <Button
            onClick={handleDelete}
            variant="danger"
            icon={<FiTrash2 />}
          >
            删除
          </Button>
        </div>
      </Modal>
    </motion.div>
  );
};

export default LicenseList;